/**
 * Sentry configuration for frontend error tracking and performance monitoring.
 */
import * as Sentry from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { useEffect } from 'react';

/**
 * Configuration interface for Sentry initialization
 */
interface SentryConfig {
  dsn?: string;
  environment?: string;
  release?: string;
  enableTracing?: boolean;
  tracesSampleRate?: number;
  replaysSessionSampleRate?: number;
  replaysOnErrorSampleRate?: number;
}

/**
 * Get Sentry configuration from environment variables
 */
const getSentryConfig = (): SentryConfig => {
  return {
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'production',
    release: import.meta.env.VITE_APP_VERSION || 'dev',
    enableTracing: import.meta.env.VITE_SENTRY_ENABLE_TRACING !== 'false',
    tracesSampleRate: parseFloat(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '1.0'
    ),
    replaysSessionSampleRate: parseFloat(
      import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || '0.1'
    ),
    replaysOnErrorSampleRate: parseFloat(
      import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || '1.0'
    ),
  };
};

/**
 * Initialize Sentry SDK with React and React Router integrations
 */
export const initSentry = (): void => {
  const config = getSentryConfig();

  // Skip initialization if DSN is not provided
  if (!config.dsn) {
    console.info('Sentry DSN not configured. Skipping Sentry initialization.');
    return;
  }

  try {
    Sentry.init({
      dsn: config.dsn,
      environment: config.environment,
      release: config.release,

      // Integrations
      integrations: [
        // React Router integration for automatic transaction names
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),

        // Session Replay - record user sessions for debugging
        Sentry.replayIntegration({
          maskAllText: true, // Mask all text for privacy
          blockAllMedia: true, // Block media for privacy
        }),

        // Breadcrumbs for better context
        Sentry.breadcrumbsIntegration({
          console: true,
          dom: true,
          fetch: true,
          history: true,
          xhr: true,
        }),
      ],

      // Performance Monitoring
      tracesSampleRate: config.enableTracing ? config.tracesSampleRate : 0,

      // Session Replay sampling
      replaysSessionSampleRate: config.replaysSessionSampleRate,
      replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,

      // Send default PII (user info)
      sendDefaultPii: true,

      // Before send hook to filter out sensitive data
      beforeSend(event, hint) {
        // Don't send events in development
        if (import.meta.env.DEV) {
          console.error('Sentry Event (DEV):', event, hint);
          return null;
        }

        // Filter out sensitive data from breadcrumbs
        if (event.breadcrumbs) {
          event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
            if (breadcrumb.data) {
              // Remove sensitive headers
              if (breadcrumb.data.Authorization) {
                breadcrumb.data.Authorization = '[REDACTED]';
              }
              // Remove sensitive query params
              if (breadcrumb.data.url) {
                try {
                  // Try to parse URL - will only work for absolute URLs
                  const url = new URL(breadcrumb.data.url);
                  if (url.searchParams.has('token')) {
                    url.searchParams.set('token', '[REDACTED]');
                    breadcrumb.data.url = url.toString();
                  }
                } catch (e) {
                  // Ignore errors for relative URLs or invalid URL formats
                  // These breadcrumbs will be kept as-is
                }
              }
            }
            return breadcrumb;
          });
        }

        return event;
      },

      // Ignore certain errors
      ignoreErrors: [
        // Network errors
        'NetworkError',
        'Network request failed',
        'Failed to fetch',
        // Browser extensions
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        // Common non-critical errors
        'Non-Error promise rejection captured',
        'cancelled',
        // Vite CSS preload errors (Mobile Safari compatibility issue)
        /Unable to preload CSS/,
      ],
    });

    console.info(
      `Sentry initialized (environment: ${config.environment}, release: ${config.release})`
    );
  } catch (error) {
    console.error('Failed to initialize Sentry:', error);
  }
};

/**
 * Set user context for Sentry events
 */
export const setSentryUser = (user: {
  id?: string;
  email?: string;
  username?: string;
}) => {
  Sentry.setUser(user);
};

/**
 * Clear user context from Sentry
 */
export const clearSentryUser = () => {
  Sentry.setUser(null);
};

/**
 * Set custom context for Sentry events
 */
export const setSentryContext = (key: string, value: Record<string, any>) => {
  Sentry.setContext(key, value);
};

/**
 * Add breadcrumb for debugging
 */
export const addSentryBreadcrumb = (
  message: string,
  category: string,
  level: 'debug' | 'info' | 'warning' | 'error' = 'info',
  data?: Record<string, any>
) => {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
  });
};

/**
 * Capture exception manually
 */
export const captureException = (
  error: Error,
  context?: Record<string, any>
) => {
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
};

/**
 * Capture message manually
 */
export const captureMessage = (
  message: string,
  level: 'debug' | 'info' | 'warning' | 'error' = 'info',
  context?: Record<string, any>
) => {
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
      Sentry.captureMessage(message, level);
    });
  } else {
    Sentry.captureMessage(message, level);
  }
};

/**
 * Start a new span for performance monitoring
 */
export const startTransaction = (name: string, op: string) => {
  return Sentry.startSpanManual({ name, op }, (span) => span);
};

/**
 * Export Sentry instance for advanced usage
 */
export { Sentry };
